import { ROLE_LABELS } from "@eca/shared";
import { KeyRound, RefreshCw, ShieldCheck, Smartphone } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Field, Input } from "@/components/ui/form";
import { InfoTable } from "@/components/ui/info-table";
import { Notice } from "@/components/ui/notice";
import { Page } from "@/components/ui/page";
import { Panel } from "@/components/ui/panel";
import { authClient, authErrorMessage } from "@/lib/auth-client";
import { useMe, useRefreshMe } from "@/lib/session";

export function AccountPage() {
  const { data: me } = useMe();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [codesPassword, setCodesPassword] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [appPassword, setAppPassword] = useState("");
  const confirmDialog = useConfirm();
  const refreshMe = useRefreshMe();
  const navigate = useNavigate();
  if (!me) return null;

  // A new phone: drop the old registration, then register again as on the first sign-in.
  const changeApp = async () => {
    const ok = await confirmDialog({
      title: "認証アプリを登録し直しますか？",
      body: "今の登録は使えなくなります。続けて、新しい認証アプリで QR コードを読み取るか、表示される文字を入力して登録します。登録が終わるまで、ほかの画面は使えません。",
      confirmLabel: "登録し直す",
    });
    if (!ok) return;
    const { error } = await authClient.twoFactor.disable({ password: appPassword });
    if (error) return toast.error(authErrorMessage(error));
    setAppPassword("");
    await refreshMe();
    navigate("/setup-2fa", { replace: true });
  };

  const changePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (next.length < 12) return toast.error("新しいパスワードは12文字以上にしてください");
    if (next !== confirm) return toast.error("確認用のパスワードが一致しません");
    setBusy(true);
    const { error } = await authClient.changePassword({ currentPassword: current, newPassword: next, revokeOtherSessions: true });
    setBusy(false);
    if (error) return toast.error(authErrorMessage(error));
    setCurrent("");
    setNext("");
    setConfirm("");
    toast.success("パスワードを変更しました。ほかの端末はログアウトされました");
  };

  const regenerate = async () => {
    const { data, error } = await authClient.twoFactor.generateBackupCodes({ password: codesPassword });
    if (error || !data) return toast.error(authErrorMessage(error));
    setCodes(data.backupCodes);
    setCodesPassword("");
  };

  return (
    <Page art="feature-secure-onboarding" title="アカウント設定" crumbs={[{ label: "アカウント設定" }]}>
      <Panel title="基本情報">
        <InfoTable
          rows={[
            { label: "氏名", value: me.user.name },
            { label: "メールアドレス", value: me.user.email },
            { label: "役割", value: ROLE_LABELS[me.user.platformRole] },
            { label: "二段階認証", value: <span className="inline-flex items-center gap-1 font-bold text-ok"><ShieldCheck className="size-4" />有効</span> },
          ]}
        />
      </Panel>

      <Panel title="パスワードの変更">
        <form noValidate onSubmit={changePassword}>
          <Field label="現在のパスワード" required>{(id) => <Input id={id} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />}</Field>
          <Field label="新しいパスワード" required hint="12文字以上">{(id) => <Input id={id} type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />}</Field>
          <Field label="新しいパスワード（確認）" required>{(id) => <Input id={id} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}</Field>
          <div className="pt-4 text-right">
            <Button type="submit" loading={busy} disabled={!current || !next}>
              <KeyRound />
              変更する
            </Button>
          </div>
        </form>
      </Panel>

      <Panel title="認証アプリの変更" description="スマートフォンを替えたときなどに、新しい認証アプリに登録し直します。">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-0 flex-1 basis-56">
            <Input type="password" placeholder="現在のパスワード" aria-label="認証アプリの変更のための現在のパスワード" autoComplete="current-password" value={appPassword} onChange={(e) => setAppPassword(e.target.value)} />
          </div>
          <Button variant="outline" onClick={() => void changeApp()} disabled={!appPassword}>
            <Smartphone />
            登録し直す
          </Button>
        </div>
      </Panel>

      <Panel title="バックアップコードの再発行" description="以前のコードはすべて使えなくなります。">
        {codes ? (
          <div className="space-y-3">
            <Notice tone="warn" title="新しいバックアップコードを保存してください" />
            <ul className="grid grid-cols-1 gap-2 rounded-md border border-line bg-ground p-3 font-mono text-sm min-[360px]:grid-cols-2 sm:grid-cols-5">
              {codes.map((c) => <li key={c} className="text-center">{c}</li>)}
            </ul>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-0 flex-1 basis-56">
              <Input type="password" placeholder="現在のパスワード" aria-label="現在のパスワード" value={codesPassword} onChange={(e) => setCodesPassword(e.target.value)} />
            </div>
            <Button variant="outline" onClick={() => void regenerate()} disabled={!codesPassword}>
              <RefreshCw />
              再発行する
            </Button>
          </div>
        )}
      </Panel>
    </Page>
  );
}
